import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categoriesAPI } from '../api/categories';
import { Category } from '../types/product';
import { getLocalizedField } from '../utils/localize';
import type { SupportedLanguage } from '../i18n';

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: calc(var(--header-height) + 2rem);
  padding-bottom: 2rem;
`;

const PageTitle = styled.h1`
  text-align: center;
  color: var(--primary-color);
  margin-bottom: 3rem;
  font-size: clamp(1.75rem, 5vw, 2.5rem);
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const CategoryCard = styled(Link)`
  background-color: var(--white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  height: 280px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const CategoryImage = styled.div<{ $bgImage?: string }>`
  height: 200px;
  background: ${props => props.$bgImage
    ? `url(${props.$bgImage}) center/cover`
    : 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))'};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
  }
`;

const CategoryIcon = styled.span`
  font-size: 3rem;
  z-index: 1;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
`;

const CategoryInfo = styled.div`
  padding: 1.25rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: var(--white);
`;

const CategoryName = styled.h3`
  font-size: 1.25rem;
  color: var(--text-color);
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const CategoryDescription = styled.p`
  font-size: 0.9rem;
  color: var(--gray-dark);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const SkeletonCard = styled.div`
  background-color: var(--white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 280px;
`;

const Skeleton = styled.div`
  background: linear-gradient(
    90deg,
    var(--gray-light) 0%,
    #f0f0f0 50%,
    var(--gray-light) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--gray-dark);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 1.2rem;
`;

// Category icons mapping
const categoryIcons: { [key: string]: string } = {
  'Birthday Cakes': '🎂',
  'Wedding Cakes': '💒',
  'Cupcakes': '🧁',
  'Custom Cakes': '🎨',
  'Seasonal': '🎄',
  'Default': '🍰',
};

const getCategoryIcon = (categoryName: string): string => {
  return categoryIcons[categoryName] || categoryIcons['Default'];
};

const CategoriesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoriesAPI.getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="container">
          <PageTitle>{t('categories.pageTitle')}</PageTitle>
          <LoadingContainer>
            {[...Array(6)].map((_, index) => (
              <SkeletonCard key={index}>
                <Skeleton style={{ height: '200px' }} />
                <div style={{ padding: '1.25rem' }}>
                  <Skeleton style={{ height: '1.5rem', marginBottom: '0.5rem' }} />
                  <Skeleton style={{ height: '2.5rem' }} />
                </div>
              </SkeletonCard>
            ))}
          </LoadingContainer>
        </div>
      </PageContainer>
    );
  }

  if (categories.length === 0) {
    return (
      <PageContainer>
        <div className="container">
          <PageTitle>{t('categories.pageTitle')}</PageTitle>
          <EmptyMessage>
            <EmptyIcon>📦</EmptyIcon>
            <EmptyText>{t('categories.emptyState')}</EmptyText>
          </EmptyMessage>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="container">
        <PageTitle>{t('categories.pageTitle')}</PageTitle>
        <CategoriesGrid>
          {categories.map((category) => {
            const localizedName = getLocalizedField(category, 'name', language) as string;
            const localizedDescription = getLocalizedField(category, 'description', language) as string | undefined;
            return (
              <CategoryCard
                key={category.category_id}
                to={`/products?category=${category.category_id}`}
              >
                <CategoryImage $bgImage={category.image_url}>
                  {/* categoryIcons is keyed by the English category name, so the icon
                      lookup always uses the raw (non-localized) name. */}
                  <CategoryIcon>{getCategoryIcon(category.name)}</CategoryIcon>
                </CategoryImage>
                <CategoryInfo>
                  <CategoryName>{localizedName}</CategoryName>
                  <CategoryDescription>
                    {localizedDescription || t('category.browseSelectionOf', { name: localizedName.toLowerCase() })}
                  </CategoryDescription>
                </CategoryInfo>
              </CategoryCard>
            );
          })}
        </CategoriesGrid>
      </div>
    </PageContainer>
  );
};

export default CategoriesPage;